<script>
import { deletePlan, updatePlan } from '@/api/billing.js';

export default {
    props: {
        plan: {
            type: Object,
            required: true,
        },
        getPlanTypeBadge: {
            type: Function,
            required: true,
        },
    },
    methods: {
        editPlan() {
            this.$store.commit('PLAN_EDIT_SET', this.plan);
            this.$store.commit('IS_SHOW_PLAN_EDIT', true);
        },
        togglePlanStatus() {
            if (this.plan.is_active) {
                this.showDeactivateConfirmation();
            } else {
                this.showActivateConfirmation();
            }
        },
        showDeactivateConfirmation() {
            this.$store.commit(
                'CONFIRMED_POPUP_MESSAGE',
                'The plan will be hidden from users. This can be undone.'
            );
            this.$store.commit('SHOP_POPUP_NAME', 'confirmed');
            this.$store.commit(
                'CONFIRMED_POPUP_METHOD',
                this.confirmDeactivate.bind(this)
            );
        },
        showActivateConfirmation() {
            this.$store.commit(
                'CONFIRMED_POPUP_MESSAGE',
                'The plan will be visible to users again.'
            );
            this.$store.commit('SHOP_POPUP_NAME', 'confirmed');
            this.$store.commit(
                'CONFIRMED_POPUP_METHOD',
                this.confirmActivate.bind(this)
            );
        },
        async confirmDeactivate() {
            try {
                const response = await deletePlan(this.plan.id);

                if (response) {
                    this.$store.commit('POPUP_ADD', {
                        title: 'PLAN DEACTIVATED',
                        description: [
                            'Plan has been deactivated and hidden from users',
                        ],
                        color: '#00d25b',
                    });
                    await this.$store.dispatch('loadBillingPlans');
                }
            } catch (error) {
                console.error('Error deactivating plan:', error);
            }
        },
        async confirmActivate() {
            try {
                const response = await updatePlan(this.plan.id, {
                    is_active: true,
                });

                if (response) {
                    this.$store.commit('POPUP_ADD', {
                        title: 'PLAN ACTIVATED',
                        description: [
                            'Plan has been activated and is now visible to users',
                        ],
                        color: '#00d25b',
                    });
                    await this.$store.dispatch('loadBillingPlans');
                }
            } catch (error) {
                console.error('Error activating plan:', error);
            }
        },
        formatPrice() {
            const amount = (this.plan.price_amount / 100).toFixed(2);
            return `${this.plan.price_currency} ${amount}`;
        },
    },
};
</script>

<template>
    <tr :class="{ inactive: !plan.is_active }">
        <td>{{ plan.id }}</td>
        <td>
            <span class="status_badge" :class="{ active: plan.is_active }">
                {{ plan.is_active ? 'Active' : 'Inactive' }}
            </span>
        </td>
        <td>
            <span
                class="type_badge"
                :class="getPlanTypeBadge(plan.plan_type).class"
            >
                {{ getPlanTypeBadge(plan.plan_type).text }}
            </span>
        </td>
        <td class="plan_name">{{ plan.name }}</td>
        <td>{{ formatPrice() }}</td>
        <td>{{ plan.billing_period }}</td>
        <td>{{ plan.trial_days }} days</td>
        <td>{{ plan.max_downloads }}</td>
        <td>{{ plan.active_subscriptions || 0 }}</td>
        <td class="actions_cell">
            <button class="button_blue_100" @click="editPlan">Edit</button>
            <button
                class="button_blue_100"
                :class="plan.is_active ? 'button_red' : 'button_green'"
                @click="togglePlanStatus"
            >
                {{ plan.is_active ? 'Deactivate' : 'Activate' }}
            </button>
        </td>
    </tr>
</template>

<style scoped>
tr {
    border-bottom: 1px solid var(--border-color);
}

tr:hover td {
    background: var(--background-hover);
}

tr.inactive {
    opacity: 0.6;
}

td {
    padding: 12px 15px;
    color: var(--text-color-primary);
}

.plan_name {
    font-weight: 600;
}

.status_badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    background: rgba(200, 50, 50, 0.2);
    color: #c03232;
}

.status_badge.active {
    background: rgba(0, 210, 91, 0.2);
    color: #00d25b;
}

.actions_cell {
    display: flex;
    gap: 8px;
}

.type_badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.badge_personal {
    background: rgba(33, 150, 243, 0.2);
    color: #2196f3;
}

.badge_corporate {
    background: rgba(156, 39, 176, 0.2);
    color: #9c27b0;
}

.badge_unknown {
    background: rgba(158, 158, 158, 0.2);
    color: #9e9e9e;
}
</style>
